import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { Download, Search, Filter, Calendar, Bot, Image as ImageIcon, Loader2, X, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';

interface AIAgentOutput {
  id: string;
  user_id: string;
  agent_id: string;
  prompt: string;
  output_url: string;
  created_at: string;
  generation_type: 'text' | 'image';
  llm_model: string;
  metadata: any;
  agent_name?: string;
}

interface AgentOutputsProps {
  navigate: (to: string) => void;
}

export default function AgentOutputs({ navigate }: AgentOutputsProps) {
  const [outputs, setOutputs] = useState<AIAgentOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<'all' | 'text' | 'image'>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [agents, setAgents] = useState<{ id: string; name: string; agent_type?: string }[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOutput, setSelectedOutput] = useState<AIAgentOutput | null>(null);
  const [outputContent, setOutputContent] = useState<string | null>(null);
  const [deletingOutput, setDeletingOutput] = useState<string | null>(null);

  useEffect(() => {
    fetchAgents();
    fetchOutputs();
  }, []);

  const fetchAgents = async () => {
    try {
      // Get all unique agents from the ai_agents table
      const { data: customAgents, error } = await supabase
        .from('ai_agents')
        .select('id, name, agent_type')
        .order('name');

      if (error) {
        console.error('Error fetching agents:', error);
        toast.error('Failed to load agents');
        setAgents([]);
        return;
      }

      // Define pre-built agents
      const prebuiltAgents = [
        { id: '00000000-0000-0000-0000-000000000001', name: 'Project Methodology Advisor', type: 'text' },
        { id: '00000000-0000-0000-0000-000000000002', name: 'Project Charter Generator', type: 'text' },
        { id: '00000000-0000-0000-0000-000000000003', name: 'Gantt Chart Generator', type: 'text' },
        { id: '00000000-0000-0000-0000-000000000004', name: 'Meeting Agenda Planner', type: 'text' },
        { id: '00000000-0000-0000-0000-000000000005', name: 'Meeting Summarizer', type: 'text' },
        { id: '00000000-0000-0000-0000-000000000006', name: 'Customer Mood Analyzer', type: 'text' },
        { id: '00000000-0000-0000-0000-000000000007', name: 'Productivity Improvement Planner', type: 'text' },
        { id: '00000000-0000-0000-0000-000000000008', name: 'Team Standup Summarizer', type: 'text' }
      ];

      // Use a Set to track agent IDs and ensure uniqueness
      const agentSet = new Set<string>();
      const uniqueAgents: { id: string; name: string; agent_type?: string }[] = [];

      // Add custom agents first
      customAgents?.forEach(agent => {
        if (agent.id && !agentSet.has(agent.id)) {
          uniqueAgents.push({
            id: agent.id,
            name: agent.name || 'Unknown Agent',
            agent_type: agent.agent_type
          });
          agentSet.add(agent.id);
        }
      });

      // Add pre-built agents
      prebuiltAgents.forEach(agent => {
        if (!agentSet.has(agent.id)) {
          uniqueAgents.push({
            id: agent.id,
            name: agent.name,
            agent_type: agent.type
          });
          agentSet.add(agent.id);
        }
      });

      // Sort alphabetically by name
      uniqueAgents.sort((a, b) => a.name.localeCompare(b.name));
      setAgents(uniqueAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load agents');
      setAgents([]);
    }
  };

  const fetchOutputs = async () => {
    try {
      setLoading(true);
      // First check if the table exists
      const { error: tableCheckError } = await supabase
        .from('ai_agent_outputs')
        .select('id')
        .limit(1);

      if (tableCheckError) {
        console.error('Error checking ai_agent_outputs table:', tableCheckError);
        setOutputs([]);
        setLoading(false);
        return;
      }

      // Fetch the outputs with a limit to prevent loading too many at once
      const { data: outputsData, error } = await supabase
        .from('ai_agent_outputs')
        .select(`
          *,
          ai_agents (
            name
          )
        `)
        .limit(50)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include agent_name
      const transformedData = outputsData?.map(item => ({
        ...item,
        agent_name: item.ai_agents?.name || 'Unknown Agent'
      })) || [];

      setOutputs(transformedData);
    } catch (error) {
      console.error('Error fetching outputs:', error);
      toast.error('Failed to load agent outputs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void applyFilters();
  };

  const applyFilters = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('ai_agent_outputs')
        .select(`
          *,
          ai_agents (
            name
          )
        `);

      // Apply agent filter
      if (selectedAgentId) {
        query = query.eq('agent_id', selectedAgentId);
      }

      // Apply date range filter
      if (dateRange.start) {
        query = query.gte('created_at', dateRange.start);
      }
      if (dateRange.end) {
        // Add one day to include the end date fully
        const endDate = new Date(dateRange.end);
        endDate.setDate(endDate.getDate() + 1);
        query = query.lt('created_at', endDate.toISOString());
      }

      // Execute the query
      const { data, error } = await query
        .limit(50)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include agent_name
      let transformedData = data?.map(item => ({
        ...item,
        agent_name: item.ai_agents?.name || 'Unknown Agent'
      })) || [];

      // Apply search filter client-side (for more flexible searching)
      if (searchQuery) {
        const lowerQuery = searchQuery.toLowerCase();
        transformedData = transformedData.filter(
          output => 
            output.prompt.toLowerCase().includes(lowerQuery) ||
            output.agent_name.toLowerCase().includes(lowerQuery) ||
            output.llm_model.toLowerCase().includes(lowerQuery)
        );
      }

      setOutputs(transformedData);
    } catch (error) {
      console.error('Error applying filters:', error);
      toast.error('Failed to filter outputs');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedAgentId('');
    setSelectedType('all');
    setDateRange({ start: '', end: '' });
    fetchOutputs();
  };

  const viewOutput = async (output: AIAgentOutput) => {
    setSelectedOutput(output);

    if (output.generation_type === 'text') {
      try {
        // Check if the output_url is a valid URL or raw text content
        let content = '';
        if (output.output_url.startsWith('http')) {
          const response = await fetch(output.output_url);
          if (!response.ok) {
            throw new Error('Failed to fetch output content');
          }
          content = await response.text();
        } else {
          // If it's not a URL, it might be the content itself
          content = output.output_url;
        }
        setOutputContent(content);
      } catch (error) {
        console.error('Error fetching output content:', error);
        toast.error('Failed to load output content');
        setOutputContent(null);
      }
    } else {
      // For image outputs, we don't need to fetch content
      setOutputContent(null);
    }
  };

  const downloadOutput = async (output: AIAgentOutput) => {
    try {
      if (output.generation_type === 'text') {
        // For text outputs, download as PDF file
        let content = '';
        if (output.output_url.startsWith('http')) {
          const response = await fetch(output.output_url);
          if (!response.ok) {
            throw new Error('Failed to fetch output content');
          }
          content = await response.text();
        } else {
          // If it's not a URL, it might be the content itself
          content = output.output_url;
        }
        
        // Create PDF with proper formatting
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 20;
        
        // Add header with title
        pdf.setFillColor(79, 70, 229); // Indigo color
        pdf.rect(0, 0, pageWidth, 40, 'F');
        
        // Add title with white text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        const title = `${output.agent_name} Output`;
        const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
        const titleX = (pageWidth - titleWidth) / 2;
        pdf.text(title, titleX, 25);
        
        // Reset text color for content
        pdf.setTextColor(0, 0, 0);
        
        // Start position after header
        let y = 50;
        
        // Add prompt section
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setFillColor(240, 247, 255); // Light blue background
        pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
        pdf.text('Prompt:', margin, y);
        y += 10;
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        const wrappedPrompt = pdf.splitTextToSize(output.prompt, pageWidth - (margin * 2));
        pdf.text(wrappedPrompt, margin, y);
        y += wrappedPrompt.length * 7 + 15;
        
        // Add content section
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setFillColor(240, 247, 255); // Light blue background
        pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
        pdf.text('Response:', margin, y);
        y += 10;
        
        // Check if content is HTML
        if (content.trim().startsWith('<')) {
          // Parse HTML content
          const parser = new DOMParser();
          const doc = parser.parseFromString(content, 'text/html');
          
          // Process each heading and its content
          const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
          
          if (headings.length === 0) {
            // If no headings, just add the text content
            const textContent = doc.body.textContent?.trim() || '';
            const wrappedContent = pdf.splitTextToSize(textContent, pageWidth - (margin * 2));
            
            // Check if we need a new page
            if (y + wrappedContent.length * 7 > pdf.internal.pageSize.getHeight() - 20) {
              pdf.addPage();
              y = 20;
            }
            
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            pdf.text(wrappedContent, margin, y);
          } else {
            headings.forEach((heading) => {
              // Check if we need a new page
              if (y > pdf.internal.pageSize.getHeight() - 40) {
                pdf.addPage();
                y = 20;
              }
              
              // Set heading font
              const level = parseInt(heading.tagName.substring(1));
              pdf.setFontSize(16 - (level - 1) * 2); // h1=16, h2=14, h3=12, etc.
              pdf.setFont('helvetica', 'bold');
              
              // Add background for headings
              if (level <= 3) {
                pdf.setFillColor(240, 247, 255); // Light blue background
                pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
              }
              
              const headingText = heading.textContent?.trim() || '';
              pdf.text(headingText, margin, y);
              y += 12;
              
              // Process content until next heading
              let nextElement = heading.nextElementSibling;
              while (nextElement && !['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(nextElement.tagName)) {
                // Check if we need a new page
                if (y > pdf.internal.pageSize.getHeight() - 20) {
                  pdf.addPage();
                  y = 20;
                }
                
                if (nextElement.tagName === 'P') {
                  pdf.setFontSize(11);
                  pdf.setFont('helvetica', 'normal');
                  const paragraphText = nextElement.textContent?.trim() || '';
                  const wrappedText = pdf.splitTextToSize(paragraphText, pageWidth - (margin * 2));
                  pdf.text(wrappedText, margin, y);
                  y += wrappedText.length * 7;
                } else if (nextElement.tagName === 'UL' || nextElement.tagName === 'OL') {
                  // Process list items
                  const items = nextElement.querySelectorAll('li');
                  items.forEach((item, index) => {
                    // Check if we need a new page
                    if (y > pdf.internal.pageSize.getHeight() - 20) {
                      pdf.addPage();
                      y = 20;
                    }
                    
                    pdf.setFontSize(11);
                    pdf.setFont('helvetica', 'normal');
                    
                    // Add bullet point or number
                    const prefix = nextElement.tagName === 'OL' ? `${index + 1}.` : '•';
                    pdf.text(prefix, margin, y);
                    
                    const itemText = item.textContent?.trim() || '';
                    const wrappedItem = pdf.splitTextToSize(itemText, pageWidth - (margin * 2) - 10);
                    pdf.text(wrappedItem, margin + 10, y);
                    y += wrappedItem.length * 7;
                  });
                } else if (nextElement.tagName === 'PRE') {
                  // Code blocks
                  pdf.setFontSize(10);
                  pdf.setFont('courier', 'normal');
                  pdf.setFillColor(245, 247, 250);
                  
                  const codeText = nextElement.textContent?.trim() || '';
                  const wrappedCode = pdf.splitTextToSize(codeText, pageWidth - (margin * 2) - 10);
                  
                  // Background for code
                  pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, wrappedCode.length * 7 + 10, 'F');
                  
                  pdf.text(wrappedCode, margin, y);
                  y += wrappedCode.length * 7 + 5;
                  
                  // Reset font
                  pdf.setFont('helvetica', 'normal');
                  pdf.setFontSize(11);
                }
                
                nextElement = nextElement.nextElementSibling;
                y += 3; // Add some space between elements
              }
              
              y += 8; // Add space after section
            });
          }
        } else {
          // Plain text content
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          const cleanContent = content.replace(/\n{3,}/g, '\n\n').trim(); // Remove excessive newlines
          const wrappedContent = pdf.splitTextToSize(cleanContent, pageWidth - (margin * 2));
          
          // Check if we need a new page
          if (y + wrappedContent.length * 7 > pdf.internal.pageSize.getHeight() - 20) {
            pdf.addPage();
            y = 20;
          }
          
          pdf.text(wrappedContent, margin, y);
        }
        
        // Add footer with date
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        
        // Add footer to all pages
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          pdf.setFontSize(9);
          pdf.setTextColor(128, 128, 128);
          pdf.text(`Generated by ${output.agent_name} on ${new Date(output.created_at).toLocaleDateString()} | Page ${i} of ${pageCount}`, margin, pdf.internal.pageSize.getHeight() - 10);
        }
        
        // Save the PDF
        pdf.save(`${output.agent_name.toLowerCase().replace(/\s+/g, '-')}-output-${new Date(output.created_at).toISOString().split('T')[0]}.pdf`);
      } else {
        // For image outputs, download the image
        const response = await fetch(output.output_url);
        if (!response.ok) {
          throw new Error('Failed to fetch image');
        }
        const blob = await response.blob();
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${output.agent_name}-image-${new Date(output.created_at).toISOString().split('T')[0]}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      toast.success('Output downloaded successfully');
    } catch (error) {
      console.error('Error downloading output:', error);
      toast.error('Failed to download output');
    }
  };

  const handleDeleteOutput = async (outputId: string) => {
    if (!confirm('Are you sure you want to delete this output?')) {
      return;
    }

    setDeletingOutput(outputId);
    try {
      const { error } = await supabase
        .from('ai_agent_outputs')
        .delete()
        .eq('id', outputId);

      if (error) throw error;
      
      setOutputs(outputs.filter(output => output.id !== outputId));
      toast.success('Output deleted successfully');
      
      // If the deleted output is currently selected, close the modal
      if (selectedOutput?.id === outputId) {
        setSelectedOutput(null);
        setOutputContent(null);
      }
    } catch (error) {
      console.error('Error deleting output:', error);
      toast.error('Failed to delete output');
    } finally {
      setDeletingOutput(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-0">AI Agent Outputs</h1>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6 ${showFilters ? 'block' : 'hidden'}`}>
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Search
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="search"
                      className="block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Search by prompt or agent name"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="w-full sm:w-1/2">
                  <label htmlFor="agent" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Select Agent
                  </label>
                  <select
                    id="agent"
                    className="block w-full sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                  >
                    <option value="">All Agents</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      id="start-date"
                      className="block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    />
                  </div>
                </div>

                <div className="w-full sm:w-1/2">
                  <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      id="end-date"
                      className="block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Apply Filters
                </button>
              </div>
            </form>
          </div>

          {/* Outputs Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : outputs.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                No AI agent outputs found.
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                Use AI agents to generate outputs that will appear here.
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {outputs.map((output) => (
                <div
                  key={output.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        {output.generation_type === 'text' ? (
                          <Bot className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
                        ) : (
                          <ImageIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
                        )}
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                          {output.agent_name}
                        </h3>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        output.generation_type === 'text'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200'
                      }`}>
                        {output.generation_type === 'text' ? 'Text' : 'Image'}
                      </span>
                    </div>
                    
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prompt:</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                        {output.prompt}
                      </p>
                    </div>
                    
                    {output.generation_type === 'image' && (
                      <div className="mb-4 flex justify-center">
                        <img 
                          src={output.output_url} 
                          alt="Generated image" 
                          className="h-32 object-contain rounded-md"
                          onClick={() => viewOutput(output)}
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{new Date(output.created_at).toLocaleString()}</span>
                      <span>{output.llm_model}</span>
                    </div>
                    
                    <div className="mt-4 flex justify-end space-x-2">
                      <button
                        onClick={() => viewOutput(output)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300 dark:hover:bg-indigo-900/70"
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" />
                        View
                      </button>
                      <button
                        onClick={() => downloadOutput(output)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        <Download className="h-3.5 w-3.5 mr-1" />
                        Download
                      </button>
                      <button
                        onClick={() => handleDeleteOutput(output.id)}
                        disabled={deletingOutput === output.id}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-300 dark:hover:bg-red-900/70"
                      >
                        {deletingOutput === output.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Output Viewer Modal */}
      {selectedOutput && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {selectedOutput.agent_name} Output
                </h2>
                <button
                  onClick={() => {
                    setSelectedOutput(null);
                    setOutputContent(null);
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prompt:</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md">
                  {selectedOutput.prompt}
                </p>
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Output:</h3>
                  <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>Model: {selectedOutput.llm_model}</span>
                    <span>•</span>
                    <span>Created: {new Date(selectedOutput.created_at).toLocaleString()}</span>
                  </div>
                </div>
                
                {selectedOutput.generation_type === 'text' ? (
                  outputContent ? (
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-md overflow-auto max-h-[50vh]">
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        {outputContent.startsWith('<') ? (
                          <div dangerouslySetInnerHTML={{ __html: outputContent }} />
                        ) : (
                          <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 dark:text-gray-200 p-0 m-0 bg-transparent border-0">{outputContent}</pre>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-center items-center h-32">
                      <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                    </div>
                  )
                ) : (
                  <div className="flex justify-center">
                    <img 
                      src={selectedOutput.output_url} 
                      alt="Generated image" 
                      className="max-h-[50vh] object-contain rounded-md"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => downloadOutput(selectedOutput)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </button>
                <button
                  onClick={() => handleDeleteOutput(selectedOutput.id)}
                  disabled={deletingOutput === selectedOutput.id}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  {deletingOutput === selectedOutput.id ? (
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}